import { useState, useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { cn } from '../../utils/cn';
import { useAuth } from '../../context/AuthContext';
import { hasAccess } from '../../lib/routes';

const SIDEBAR_KEY = 'az-sidebar-collapsed';

export default function MainLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === 'true'; } catch { return false; }
  });
  const [transitionEnabled, setTransitionEnabled] = useState(false);

  // Enable transitions only after first render to prevent CLS
  useEffect(() => {
    const id = requestAnimationFrame(() => setTransitionEnabled(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Persist sidebar state
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, String(sidebarCollapsed)); } catch { /* noop */ }
  }, [sidebarCollapsed]);
  const { user } = useAuth();
  const location = useLocation();

  // Obtener el nombre de la ruta actual (ej: /produccion -> produccion)
  const currentRoute = location.pathname.split('/')[1] || 'dashboard';

  // Verificar permisos de acceso
  if (user && !hasAccess(user.rol, currentRoute)) {
    // Mercaderistas van a su app simplificada
    if (user.rol === 'mercaderista') {
      return <Navigate to="/mi-dia" replace />;
    }
    // Visita INVIMA no tiene dashboard, redirigir a batch
    if (user.rol === 'visita_invima') {
      return <Navigate to="/batch" replace />;
    }
    // Otros roles sin acceso van al dashboard (si tienen acceso) o a la primera ruta permitida
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <div
        className={cn(
          'flex flex-col min-h-screen',
          transitionEnabled && 'transition-all duration-300',
          sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'
        )}
      >
        {/* Navbar */}
        <Navbar onMenuClick={() => setSidebarOpen(true)} />

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="py-4 px-6 border-t border-gray-200 bg-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-gray-500">
            <p>
              © {new Date().getFullYear()} Azalac SAS. Todos los derechos
              reservados.
            </p>
            <p>Hecho con amor en Cali, Colombia</p>
          </div>
        </footer>
      </div>
    </div>
  );
}
